function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import { alpha, darken } from '@mui/material';
import { Attention as AttentionIcon, CaretDown as CaretDownIcon, CaretRight as CaretRightIcon, Checkmark as CheckmarkIcon, Cross as CrossIcon, Danger as DangerIcon, Filter as FilterIcon, Info as InfoIcon, SortDown as SortDownIcon, SortUp as SortUpIcon, Success as SuccessIcon, User as UserIcon } from '@mineral/icons';
import { CheckboxChecked as CheckboxCheckedIcon, CheckboxUnchecked as CheckboxUncheckedIcon, CheckboxIndeterminate as CheckboxIndeterminateIcon } from '../Checkbox/icons';
import { RadioChecked as RadioCheckedIcon, RadioUnchecked as RadioUncheckedIcon } from '../Radio/icons';
import { Select } from '../Select';
import { TextField } from '../TextField';
import { jsx as _jsx } from "react/jsx-runtime";
export var createComponents = function createComponents(theme) {
  return {
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
        variant: 'outlined'
      },
      styleOverrides: {
        root: {
          '&:first-of-type .MuiAccordionSummary-root  ': {
            borderTopLeftRadius: parseInt("" + theme.shape.borderRadius) - 1,
            borderTopRightRadius: parseInt("" + theme.shape.borderRadius) - 1
          },
          '&:last-of-type .MuiAccordionSummary-root ': {
            borderBottomLeftRadius: parseInt("" + theme.shape.borderRadius) - 1,
            borderBottomRightRadius: parseInt("" + theme.shape.borderRadius) - 1
          }
        }
      }
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: /*#__PURE__*/_jsx(CaretDownIcon, {})
      },
      styleOverrides: {
        root: function root(_ref) {
          var disabled = _ref.ownerState.disabled;
          return _extends({
            minHeight: theme.spacing(5),
            // Override MUI default minHeight
            '&:after': {
              content: 'unset' // Remove the :after pseudo element from MuiButtonBase in order to avoid conflicts with nested MuiButtonBase elements

            }
          }, !disabled && {
            '.MuiAccordionSummary-expandIconWrapper': {
              color: theme.palette.primary.main
            }
          }, {
            '&.Mui-focusVisible, &:focus-visible': _extends({}, theme.mixins.focusRing({
              color: theme.palette.primary.main
            }), {
              background: 'transparent',
              outlineOffset: 2,
              // Needs pushed out further as border is on MuiAccordion-root
              zIndex: 1
            }),
            '&.Mui-expanded': {
              minHeight: theme.spacing(5) // Match unexpanded

            }
          });
        },
        content: {
          marginBlock: theme.spacing(1),
          // 9.5px would be exact, but minHeight fixes
          transition: 'none',
          // Remove MUI margin transition
          '&.Mui-expanded': {
            marginBlock: theme.spacing(1) // Match unexpanded

          }
        }
      }
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          error: /*#__PURE__*/_jsx(DangerIcon, {}),
          info: /*#__PURE__*/_jsx(InfoIcon, {}),
          success: /*#__PURE__*/_jsx(SuccessIcon, {}),
          warning: /*#__PURE__*/_jsx(AttentionIcon, {})
        }
      },
      styleOverrides: {
        root: function root(_ref2) {
          var _ref2$ownerState = _ref2.ownerState,
              color = _ref2$ownerState.color,
              severity = _ref2$ownerState.severity,
              variant = _ref2$ownerState.variant;
          var colorOrSeverity = color || severity;
          var newColor = colorOrSeverity && theme.palette[colorOrSeverity].main;
          var newBorderColor = newColor && alpha(newColor, 0.5);
          return _extends({}, (variant === 'standard' || variant === 'outlined') && {
            border: "1px solid " + newBorderColor,
            '.MuiAlert-icon': {
              color: newColor
            }
          });
        },
        action: {
          paddingTop: 7 // Match alert icon

        },
        icon: {
          marginRight: theme.spacing(1)
        }
      }
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 1,
        // Necessary for dark mode coloration
        square: true
      }
    },
    MuiAutocomplete: {
      defaultProps: {
        componentsProps: {
          paper: {
            elevation: 8 // Match MuiPopover elevation

          }
        },
        popupIcon: /*#__PURE__*/_jsx(CaretDownIcon, {}),
        size: 'medium'
      },
      styleOverrides: {
        root: function root(_ref3) {
          var size = _ref3.ownerState.size;
          return {
            // Increase specificity to overcome size classes
            '&& .MuiOutlinedInput-root': _extends({}, size === 'small' && {
              paddingBlock: 3.5 // Match medium Button, targeting 32px height

            }, size === 'medium' && {
              paddingBlock: 2.5 // Match large Button, targeting 40px height

            })
          };
        },
        clearIndicator: {
          '&:hover': {
            backgroundColor: 'transparent'
          }
        },
        paper: {
          fontSize: theme.typography.body2.fontSize
        },
        popupIndicator: function popupIndicator(_ref4) {
          var disabled = _ref4.ownerState.disabled;
          return {
            '.MuiSvgIcon-root': {
              color: !disabled && theme.palette.primary.main,
              fontSize: theme.typography.pxToRem(24)
            },
            '&:hover': {
              backgroundColor: 'transparent'
            }
          };
        },
        tag: {
          marginBlock: 1 // Prevent height of Autocomplete from increasing

        }
      }
    },
    MuiAvatar: {
      defaultProps: {
        children: /*#__PURE__*/_jsx(UserIcon, {}) // Replaces the default fallback icon

      },
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.neutral.main
        }
      }
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(theme.palette.common.coolBlack, 0.5)
        },
        invisible: {
          backgroundColor: 'transparent'
        }
      }
    },
    MuiBreadcrumbs: {
      defaultProps: {
        'aria-label': 'breadcrumbs',
        itemsAfterCollapse: 3,
        itemsBeforeCollapse: 0,
        maxItems: 4,
        separator: /*#__PURE__*/_jsx(CaretRightIcon, {
          fontSize: "inherit"
        })
      },
      styleOverrides: {
        root: {
          color: 'inherit',
          fontSize: theme.typography.pxToRem(12),
          // Target the collapse control, which has a private className
          '& li:not(.MuiBreadcrumbs-li):not(.MuiBreadcrumbs-separator) > .MuiButtonBase-root': {
            color: theme.palette.primary.main,
            '&.Mui-focusVisible, &:focus-visible': _extends({}, theme.mixins.focusRing({
              color: theme.palette.primary.main
            }))
          }
        },
        li: {
          '&:not(:last-child)': {
            fontWeight: theme.typography.fontWeightMedium
          }
        },
        separator: {
          color: theme.palette.text.disabled,
          fontSize: theme.typography.pxToRem(16)
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: function root(_ref5) {
          var _ref5$ownerState = _ref5.ownerState,
              color = _ref5$ownerState.color,
              size = _ref5$ownerState.size,
              variant = _ref5$ownerState.variant;
          return _extends({}, theme.typography.button, {
            paddingInline: theme.spacing(1)
          }, variant === 'contained' && {
            '&.Mui-focusVisible, &:focus-visible': _extends({}, theme.mixins.focusRing({
              color: color && color !== 'inherit' ? theme.palette[color].main : undefined
            }))
          }, (variant === 'outlined' || variant === 'text') && _extends({
            '&.Mui-focusVisible, &:focus-visible': _extends({}, theme.mixins.focusRing())
          }, theme.palette.mode === 'light' && {
            '&:active': {
              color: color && color !== 'inherit' ? darken(theme.palette[color].main, 0.08) : undefined
            }
          }), size === 'small' && {
            lineHeight: 16 / 14,
            // Target height - padding / font-size
            '&& .MuiSvgIcon-root': {
              fontSize: 16
            }
          }, size === 'medium' && {
            '&& .MuiSvgIcon-root': {
              fontSize: 20
            }
          }, size === 'large' && {
            lineHeight: 24 / 14,
            // Target height - padding / font-size
            '&& .MuiSvgIcon-root': {
              fontSize: 24
            }
          });
        },
        startIcon: {
          marginLeft: 0 // Remove negative margin

        },
        endIcon: {
          marginRight: 0 // Remove negative margin

        }
      }
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true
      },
      styleOverrides: {
        root: {
          // Replacement for focusRipple, only visible for keyboard interaction
          // https://github.com/WICG/focus-visible/blob/main/explainer.md
          '&.Mui-focusVisible, &:focus-visible': _extends({}, theme.mixins.focusRing()),
          // Active state ripple replacement
          '&:after': {
            background: 'currentColor',
            borderRadius: 'inherit',
            content: '""',
            display: 'block',
            pointerEvents: 'none',
            position: 'absolute',
            inset: 0,
            opacity: 0,
            zIndex: -1
          },
          '&:active:after': {
            opacity: 0.08,
            zIndex: 0
          }
        }
      }
    },
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
        disableFocusRipple: true
      },
      styleOverrides: {
        root: function root(_ref6) {
          var variant = _ref6.ownerState.variant;
          return _extends({}, variant === 'contained' && {
            gap: 1,
            // Increase specificity to also handle disabled state
            '&&& .MuiButton-contained': {
              borderColor: 'transparent'
            }
          }, {
            '> :nth-of-type(1n)': {
              zIndex: 1 // Set default zIndex of children

            },
            '> .Mui-focusVisible, > :focus-visible': {
              zIndex: 2 // Adjust zIndex for outline overlap on focus

            }
          });
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'column'
        }
      }
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: theme.spacing(1, 2, 2),
          justifyContent: 'flex-end'
        },
        spacing: {
          paddingBottom: theme.spacing(1),
          '&& > *': {
            marginBottom: theme.spacing(1),
            '& + *': {
              marginLeft: theme.spacing(1)
            }
          }
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          flex: '1 1 auto',
          padding: theme.spacing(1, 2, 2)
        }
      }
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          // @ts-ignore
          component: 'h3',
          variant: 'h4',
          color: 'text.secondary'
        },
        subheaderTypographyProps: {
          variant: 'caption',
          color: 'text.secondary'
        }
      },
      styleOverrides: {
        root: {
          padding: theme.spacing(2, 2, 1)
        },
        subheader: {
          marginTop: theme.spacing(1)
        }
      }
    },
    MuiCheckbox: {
      defaultProps: {
        checkedIcon: /*#__PURE__*/_jsx(CheckboxCheckedIcon, {}),
        icon: /*#__PURE__*/_jsx(CheckboxUncheckedIcon, {}),
        indeterminateIcon: /*#__PURE__*/_jsx(CheckboxIndeterminateIcon, {}),
        size: 'small'
      },
      styleOverrides: {
        root: function root(_ref7) {
          var color = _ref7.ownerState.color;
          return {
            borderRadius: 2,
            // Match Checkbox SVG radius
            margin: theme.spacing(1),
            // Allows for spacing with padding removed
            padding: 0,
            // Allows focusRing to hug shape more closely
            '&.Mui-focusVisible, &:focus-visible': _extends({}, theme.mixins.focusRing({
              color: color && color !== 'default' ? theme.palette[color].main : undefined
            }))
          };
        }
      }
    },
    MuiChip: {
      defaultProps: {
        deleteIcon: /*#__PURE__*/_jsx(CrossIcon, {})
      },
      styleOverrides: {
        root: function root(_ref8) {
          var _ref8$ownerState = _ref8.ownerState,
              _ref8$ownerState$colo = _ref8$ownerState.color,
              color = _ref8$ownerState$colo === void 0 ? 'default' : _ref8$ownerState$colo,
              variant = _ref8$ownerState.variant;
          return _extends({
            fontSize: theme.typography.pxToRem(14),
            // TODO: This causes a optical vertical alignment issue
            fontWeight: theme.typography.fontWeightMedium
          }, variant === 'filled' && {
            '&.Mui-focusVisible, &:focus-visible': _extends({}, theme.mixins.focusRing({
              color: color && color !== 'default' ? theme.palette[color].main : undefined
            }), {
              // Prevent backgroundColor change when focused
              backgroundColor: color === 'default' ? theme.palette.action.selected : theme.palette[color].main
            })
          }, variant === 'outlined' && {
            '&.Mui-focusVisible, &:focus-visible': {
              backgroundColor: 'transparent'
            }
          }, {
            '&:active': {
              boxShadow: 'none'
            }
          });
        },
        avatar: function avatar(_ref9) {
          var size = _ref9.ownerState.size;
          return _extends({
            color: theme.palette.neutral.contrastText
          }, size === 'small' && {
            height: 16,
            width: 16
          });
        },
        icon: function icon(_ref10) {
          var size = _ref10.ownerState.size;
          return _extends({}, size === 'small' && {
            fontSize: theme.typography.pxToRem(16)
          });
        },
        deleteIcon: function deleteIcon(_ref11) {
          var color = _ref11.ownerState.color;
          return _extends({
            fontSize: theme.typography.pxToRem(16)
          }, color === 'default' && {
            color: theme.palette.text.disabled,
            '&:hover, &:active': {
              color: 'currentColor'
            }
          });
        }
      }
    },
    MuiDataGrid: {
      defaultProps: {
        components: {
          BaseSelect: Select,
          BaseTextField: TextField,
          BooleanCellFalseIcon: CrossIcon,
          BooleanCellTrueIcon: CheckmarkIcon,
          ColumnFilteredIcon: FilterIcon,
          ColumnSortedAscendingIcon: SortUpIcon,
          ColumnSortedDescendingIcon: SortDownIcon
        },
        componentsProps: {
          baseTextField: {
            variant: 'outlined'
          }
        }
      },
      styleOverrides: {
        cell: {
          borderBottomColor: theme.palette.divider,
          '&.MuiDataGrid-cell--editing': {
            boxShadow: 'none'
          }
        },
        columnHeaders: {
          borderBottom: "2px solid " + theme.palette.divider,
          borderTop: "1px solid " + theme.palette.divider,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0
        },
        columnHeaderTitle: {
          fontWeight: theme.typography.fontWeightBold
        },
        columnHeaderCheckbox: {
          '.MuiDataGrid-columnHeaderTitleContainerContent': {
            overflow: 'visible' // Avoid clipping checkbox focus ring

          }
        },
        filterForm: {
          gap: theme.spacing(1)
        },
        filterFormDeleteIcon: {
          marginBottom: theme.spacing(1)
        },
        footerContainer: {
          borderTop: "1px solid " + theme.palette.divider
        },
        root: {
          border: 'none',
          borderRadius: 0
        },
        'cell--editing': {
          boxShadow: 'none'
        },
        'row--editing': {
          boxShadow: 'none'
        }
      }
    },
    MuiDialog: {
      defaultProps: {
        fullWidth: true
      },
      styleOverrides: {
        paper: {
          padding: theme.spacing(3, 0)
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: theme.spacing(3, 3, 0)
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: theme.spacing(0, 3)
        },
        dividers: {
          marginTop: theme.spacing(1),
          padding: theme.spacing(3)
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: theme.spacing(0, 3, 2)
        }
      }
    },
    MuiFormControlLabel: {
      defaultProps: {
        componentsProps: {
          typography: {
            variant: 'body2'
          }
        }
      },
      styleOverrides: {
        root: function root(_ref12) {
          var labelPlacement = _ref12.ownerState.labelPlacement;
          return _extends({}, labelPlacement === 'start' && {
            marginRight: theme.spacing(-1)
          }, labelPlacement === 'end' && {
            marginLeft: theme.spacing(-1)
          }, labelPlacement === 'top' && {
            marginBottom: theme.spacing(-1)
          }, labelPlacement === 'bottom' && {
            marginTop: theme.spacing(-1)
          });
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: _extends({}, theme.typography.caption, {
          // Affects direct imports from MUI
          marginLeft: 0,
          marginRight: 0,
          '&.Mui-disabled': {
            color: theme.palette.text.disabled
          }
        })
      }
    },
    MuiFormLabel: {
      defaultProps: {
        color: 'neutral'
      },
      styleOverrides: {
        root: function root(_ref13) {
          var color = _ref13.ownerState.color;
          return _extends({
            alignItems: 'center',
            display: 'flex',
            fontSize: theme.typography.body2.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
            gap: theme.spacing(1),
            marginBottom: parseInt(theme.spacing(1)) / 4
          }, color === 'neutral' && {
            '&:not(.Mui-error)': {
              '&.Mui-focused, &:focus': {
                color: 'currentColor' // Match InputLabel

              }
            }
          });
        },
        asterisk: {
          display: 'none'
        }
      }
    },
    MuiIconButton: {
      /*
       * Mineral's IconButton renders a Button, which is unaffected by these overrides.
       * These styles affect MUI components that render an MuiIconButton internally.
       */
      styleOverrides: {
        root: function root(_ref14) {
          var _ref14$ownerState = _ref14.ownerState,
              color = _ref14$ownerState.color,
              edge = _ref14$ownerState.edge,
              size = _ref14$ownerState.size;
          return _extends({
            padding: theme.spacing(1)
          }, color === 'default' && {
            color: theme.palette.text.secondary
          }, size === 'small' && {
            '& .MuiSvgIcon-root': {
              fontSize: theme.typography.pxToRem(16)
            },
            padding: parseInt(theme.spacing(1)) / 2
          }, size === 'medium' && {
            '& .MuiSvgIcon-root': {
              fontSize: theme.typography.pxToRem(20)
            },
            padding: parseInt(theme.spacing(1)) * 0.75
          }, size === 'large' && {
            '& .MuiSvgIcon-root': {
              fontSize: theme.typography.pxToRem(24)
            }
          }, edge === 'start' && {
            marginLeft: size === 'small' ? parseInt(theme.spacing(-1)) / 2 : theme.spacing(-1)
          }, edge === 'end' && {
            marginRight: size === 'small' ? parseInt(theme.spacing(-1)) / 2 : theme.spacing(-1)
          });
        }
      }
    },
    MuiInput: {
      defaultProps: {
        disableUnderline: true
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: function input(_ref15) {
          var _ref15$ownerState = _ref15.ownerState,
              readOnly = _ref15$ownerState.readOnly,
              size = _ref15$ownerState.size;
          return _extends({
            borderRadius: 'inherit',
            // Clips backgroundColor
            fontSize: theme.typography.body2.fontSize,
            lineHeight: 1.4375,
            // Match MUI, fixes select heights
            // Increase specificity to overcome size classes
            '&': _extends({}, size === 'small' && {
              paddingBlock: 6 // Match medium Button, targeting 32px height

            }, size === 'medium' && {
              paddingBlock: 10 // Match large Button, targeting 40px height

            }),
            // Placeholder
            '&::-webkit-input-placeholder': theme.mixins.placeholder,
            '&::-moz-placeholder': theme.mixins.placeholder,
            // Firefox 19+
            '&:-ms-input-placeholder': theme.mixins.placeholder,
            // IE 11
            '&::-ms-input-placeholder': theme.mixins.placeholder
          }, readOnly && {
            cursor: 'text'
          });
        }
      }
    },
    MuiInputLabel: {
      defaultProps: {
        disableAnimation: true,
        shrink: true // Helps with placeholder visibility

      },
      styleOverrides: {
        root: {
          display: 'flex',
          alignItems: 'baseline',
          fontSize: theme.typography.body2.fontSize,
          fontWeight: theme.typography.fontWeightMedium,
          justifyContent: 'space-between',
          maxWidth: 'none',
          pointerEvents: 'initial',
          // Restore ability to click on label to focus field
          position: 'static',
          transform: 'none',
          '&:not(.Mui-error)': {
            '&.Mui-focused, &:focus': {
              color: 'currentColor' // Color does not change on focus

            }
          }
        }
      }
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover'
      },
      styleOverrides: {
        root: {
          '&.Mui-focusVisible, &:focus-visible': _extends({}, theme.mixins.focusRing())
        }
      }
    },
    MuiListItem: {
      defaultProps: {
        alignItems: 'flex-start'
      },
      styleOverrides: {
        root: function root(_ref16) {
          var _ref16$ownerState = _ref16.ownerState,
              alignItems = _ref16$ownerState.alignItems,
              disableGutters = _ref16$ownerState.disableGutters;
          return _extends({
            '& > .MuiListItemSecondaryAction-root': {
              alignSelf: alignItems,
              marginTop: alignItems === 'flex-start' && -2,
              // 21px effective height, matching text line-height
              marginBottom: alignItems === 'flex-start' && -1 // 21px effective height, matching text line-height

            },
            '& + .MuiListItemSecondaryAction-root': {
              alignSelf: alignItems,
              marginTop: alignItems === 'flex-start' && 6
            }
          }, !disableGutters && {
            paddingInline: theme.spacing(1)
          });
        },
        container: {
          display: 'flex',
          position: 'static'
        }
      }
    },
    MuiListItemAvatar: {
      styleOverrides: {
        root: {
          marginRight: theme.spacing(1),
          marginTop: -1,
          // 21px effective height, matching text line-height
          marginBottom: -2,
          // 21px effective height, matching text line-height
          minWidth: 0
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: function root(_ref17) {
          var disableGutters = _ref17.ownerState.disableGutters;
          return _extends({
            '&.Mui-focusVisible, &:focus-visible': {
              outline: 'none'
            }
          }, !disableGutters && {
            paddingInline: theme.spacing(1)
          });
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          marginRight: theme.spacing(1),
          marginTop: -1,
          // 21px effective height, matching text line-height
          marginBottom: -2,
          // 21px effective height, matching text line-height
          minWidth: 0
        }
      }
    },
    MuiListItemSecondaryAction: {
      styleOverrides: {
        root: {
          flexShrink: 0,
          marginRight: theme.spacing(1),
          position: 'static',
          // Make item text wrapping easier to deal with
          transform: 'none'
        }
      }
    },
    MuiListItemText: {
      defaultProps: {
        primaryTypographyProps: {
          variant: 'body2'
        },
        secondaryTypographyProps: {
          color: 'text.secondary'
        }
      },
      styleOverrides: {
        root: function root(_ref18) {
          var inset = _ref18.ownerState.inset;
          return _extends({
            marginBlock: 0
          }, inset && {
            paddingLeft: theme.spacing(4)
          });
        }
      }
    },
    MuiListSubheader: {
      styleOverrides: {
        root: function root(_ref19) {
          var disableGutters = _ref19.ownerState.disableGutters;
          return _extends({}, theme.typography.h5, {
            textTransform: 'uppercase'
          }, !disableGutters && {
            paddingInline: theme.spacing(1)
          });
        }
      }
    },
    MuiMenu: {
      defaultProps: {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left'
        },
        transformOrigin: {
          vertical: parseInt(theme.sizing.menuOffset),
          horizontal: 'left'
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: function root(_ref20) {
          var disableGutters = _ref20.ownerState.disableGutters;
          return _extends({}, theme.typography.body2, {
            '&.Mui-focusVisible, &:focus-visible': {
              outline: 'none'
            },
            '& > .MuiListItemIcon-root, & > .MuiListItemAvatar-root': {
              marginTop: -1,
              // 21px effective height, matching text line-height
              marginBottom: -2,
              // 21px effective height, matching text line-height
              minWidth: 0
            }
          }, !disableGutters && {
            paddingInline: theme.spacing(1)
          });
        }
      }
    },
    MuiNativeSelect: {
      styleOverrides: {
        icon: {
          '& + .MuiInputAdornment-root': {
            paddingRight: theme.spacing(3) // Prevent overlap of caret and endAdornment

          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: function root(_ref21) {
          var _ref21$ownerState = _ref21.ownerState,
              color = _ref21$ownerState.color,
              error = _ref21$ownerState.error,
              endAdornment = _ref21$ownerState.endAdornment,
              startAdornment = _ref21$ownerState.startAdornment;
          return _extends({}, startAdornment && {
            paddingLeft: theme.spacing(1)
          }, endAdornment && {
            paddingRight: theme.spacing(1)
          }, {
            '&.Mui-disabled, &[data-readonly]': {
              backgroundColor: theme.palette.action.disabledBackground,
              borderColor: theme.palette.action.disabled
            },
            '&.Mui-focused, &:focus': _extends({}, theme.mixins.focusRing({
              color: error ? theme.palette.error.main : color ? theme.palette[color].main : undefined
            }), {
              '.MuiOutlinedInput-notchedOutline': {
                // Border does not change on focus
                borderWidth: 1,
                borderColor: alpha(theme.palette.common[theme.palette.getModeValue('coldBlack', 'coolWhite')], 0.3 // MUI default is 0.23
                )
              }
            }),
            '&:hover:not(.Mui-disabled,.Mui-error,.Mui-focused,:focus)': {
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: color ? theme.palette[color].main : undefined
              }
            },
            // Colorize Select/NativeSelect caret icon - needed as Select ownerState does not contain color
            '.MuiSelect-icon, .MuiNativeSelect-icon': {
              color: error ? theme.palette.error.main : color ? theme.palette[color].main : undefined
            }
          });
        },
        input: function input(_ref22) {
          var _ref22$ownerState = _ref22.ownerState,
              endAdornment = _ref22$ownerState.endAdornment,
              startAdornment = _ref22$ownerState.startAdornment;
          return _extends({
            paddingInline: theme.spacing(1)
          }, startAdornment && {
            paddingLeft: 0
          }, endAdornment && {
            paddingRight: 0
          });
        },
        notchedOutline: {
          // This is a fieldset element
          borderWidth: 1,
          borderColor: alpha(theme.palette.common[theme.palette.getModeValue('coldBlack', 'coolWhite')], 0.3 // MUI default is 0.23
          ),
          top: 0,
          // Adjust spacing between label and outline due to hidden legend
          legend: {
            display: 'none' // Hide the notch

          }
        }
      }
    },
    MuiPopover: {
      defaultProps: {
        transformOrigin: {
          vertical: parseInt(theme.sizing.menuOffset),
          horizontal: 'left'
        }
      }
    },
    MuiRadio: {
      defaultProps: {
        checkedIcon: /*#__PURE__*/_jsx(RadioCheckedIcon, {}),
        icon: /*#__PURE__*/_jsx(RadioUncheckedIcon, {}),
        size: 'small'
      },
      styleOverrides: {
        root: function root(_ref23) {
          var color = _ref23.ownerState.color;
          return {
            margin: theme.spacing(1),
            // Allows for spacing with padding removed
            padding: 0,
            // Allows focusRing to hug shape more closely
            '&.Mui-focusVisible, &:focus-visible': _extends({}, theme.mixins.focusRing({
              color: color && color !== 'default' ? theme.palette[color].main : undefined
            }))
          };
        }
      }
    },
    MuiScopedCssBaseline: {
      styleOverrides: {
        root: {
          fontSize: theme.typography.body2.fontSize,
          backgroundColor: 'transparent'
        }
      }
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: CaretDownIcon,
        size: 'medium',
        // Needed to affect default size
        variant: 'outlined' // Needed to affect MuiNativeSelects inside MuiDataGrid

      },
      styleOverrides: {
        icon: {
          '& + .MuiInputAdornment-root': {
            paddingRight: theme.spacing(3) // Prevent overlap of caret and endAdornment

          }
        }
      }
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: function root(_ref24) {
          var fontSize = _ref24.ownerState.fontSize;
          return _extends({}, fontSize === 'small' && {
            fontSize: theme.typography.pxToRem(16) // 1rem

          }, fontSize === 'medium' && {
            fontSize: theme.typography.pxToRem(24) // 1.5rem

          }, fontSize === 'large' && {
            fontSize: theme.typography.pxToRem(32) // 2rem

          });
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          overflow: 'visible' // Prevents focus outline from being clipped in SideNav

        }
      }
    },
    MuiTable: {
      defaultProps: {
        size: 'large'
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: function root(_ref25) {
          var _ref25$ownerState = _ref25.ownerState,
              variant = _ref25$ownerState.variant,
              padding = _ref25$ownerState.padding,
              size = _ref25$ownerState.size;
          return _extends({
            borderBottom: "1px solid " + theme.palette.divider
          }, size === 'small' && {
            paddingBlock: 2.5 // Height = 24

          }, size === 'medium' && {
            paddingBlock: 5 // Height = 32

          }, size === 'large' && {
            paddingBlock: 9 // Height = 40

          }, padding === 'checkbox' && {
            // Increase specificity to apply at any size
            '&&': {
              paddingBlock: 0,
              paddingInline: theme.spacing(1),
              width: 1 // Force a minimum size

            }
          }, variant === 'head' && {
            fontWeight: theme.typography.fontWeightBold
          });
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          // These styles are not under `MuiTableCell.head` because
          // that would also affect the `tbody > tr > th` case
          '& th': {
            borderBottom: "2px solid " + theme.palette.divider,
            borderTop: "1px solid " + theme.palette.divider,
            '& + *': {
              borderLeft: "1px dotted " + theme.palette.divider
            }
          }
        }
      }
    },
    MuiTableSortLabel: {
      defaultProps: {
        IconComponent: SortDownIcon
      }
    },
    MuiTab: {
      defaultProps: {
        iconPosition: 'start'
      },
      styleOverrides: {
        root: {
          lineHeight: 1.75,
          minHeight: 0,
          minWidth: 0,
          paddingBlock: parseInt(theme.spacing(1)) / 2,
          // 32px height for horizontal orientation
          paddingInline: theme.spacing(1),
          '&.Mui-focusVisible, &:focus-visible': {
            outlineOffset: -1
          },
          '& .MuiTab-iconWrapper': {
            fontSize: theme.typography.pxToRem(20) // Match icon size in medium button. This is the icon svg tag.

          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: function root(_ref26) {
          var _ref26$ownerState = _ref26.ownerState,
              indicatorColor = _ref26$ownerState.indicatorColor,
              orientation = _ref26$ownerState.orientation,
              textColor = _ref26$ownerState.textColor;
          return _extends({
            minHeight: 0,
            position: 'relative'
          }, orientation === 'horizontal' && {
            paddingTop: theme.spacing(1),
            // Space between tabs. Uses role rather than MuiTab-root to allow for greater flexibility
            '[role="tab"] + [role="tab"]': {
              marginLeft: theme.spacing(2)
            },
            // Bottom border
            '&::before': {
              backgroundColor: theme.palette.divider,
              bottom: 0,
              content: '""',
              height: 1,
              left: 0,
              position: 'absolute',
              right: 0
            }
          }, orientation === 'vertical' && {
            paddingTop: 0,
            // Side border
            '&::before': {
              backgroundColor: theme.palette.divider,
              bottom: 0,
              content: '""',
              position: 'absolute',
              right: 0,
              top: 0,
              width: 1
            }
          }, {
            // Some Tab styles are defined here as color props are not available in Tab
            '.MuiTab-root.Mui-selected': _extends({}, (indicatorColor === 'primary' || indicatorColor === 'secondary') && {
              backgroundColor: alpha(theme.palette[indicatorColor].main, theme.palette.action.selectedOpacity),
              '&.Mui-focusVisible, &:focus-visible': {
                outline: theme.mixins.focusRing()
              }
            }, (textColor === 'primary' || textColor === 'secondary') && {
              color: theme.palette[textColor][theme.palette.getModeValue('dark', 'light')]
            }),
            // Some TabScrollButton styles are defined here as color props are not available in TabScrollButton
            '.MuiTabScrollButton-root': _extends({}, (textColor === 'primary' || textColor === 'secondary') && {
              color: theme.palette[textColor][theme.palette.getModeValue('dark', 'light')]
            }, {
              '&:hover': {
                '& > .MuiSvgIcon-root': _extends({}, (textColor === 'primary' || textColor === 'secondary') && {
                  backgroundColor: alpha(theme.palette[textColor].main, theme.palette.action.hoverOpacity)
                })
              }
            })
          });
        },
        indicator: function indicator(_ref27) {
          var indicatorColor = _ref27.ownerState.indicatorColor;
          return {
            backgroundColor: indicatorColor && theme.palette[indicatorColor].dark,
            transition: 'none'
          };
        },
        scroller: function scroller(_ref28) {
          var orientation = _ref28.ownerState.orientation;
          return _extends({}, orientation === 'horizontal' && {
            paddingInline: theme.spacing(2)
          });
        }
      }
    },
    MuiTabScrollButton: {
      styleOverrides: {
        root: function root(_ref29) {
          var _extends2;

          var _ref29$ownerState = _ref29.ownerState,
              direction = _ref29$ownerState.direction,
              orientation = _ref29$ownerState.orientation;
          var orientationDirection = orientation === 'vertical' ? direction === 'left' ? 'top' : 'bottom' : direction;
          return {
            height: 'auto',
            opacity: 1,
            padding: parseInt(theme.spacing(1)) / 2,
            position: 'relative',
            width: 'auto',
            zIndex: 2,
            // Gradient fade
            '&:not(.Mui-disabled)::before': _extends((_extends2 = {
              backgroundImage: "linear-gradient(to " + orientationDirection + ", rgba(255,255,255,0), " + theme.palette.background.paper + ")",
              content: '""',
              position: 'absolute'
            }, _extends2[orientationDirection] = '100%', _extends2), orientation === 'horizontal' && {
              bottom: 1,
              top: 0,
              width: '50%'
            }, orientation === 'vertical' && {
              height: '50%',
              left: 0,
              right: 1
            }),
            '&.Mui-disabled': {
              color: theme.palette.text.disabled,
              opacity: 1
            }
          };
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: function root(_ref30) {
          var hiddenLabel = _ref30.ownerState.hiddenLabel;
          return _extends({}, hiddenLabel && {
            '& .MuiInputLabel-root': _extends({}, theme.mixins.visuallyHidden)
          });
        }
      }
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true
      },
      styleOverrides: {
        popper: function popper(_ref31) {
          var placement = _ref31.ownerState.placement;
          return placement && {
            '&&[data-popper-placement] .MuiTooltip-tooltip': _extends({}, /top|bottom/.test(placement) && {
              marginBlock: 7
            }, /left|right/.test(placement) && {
              marginInline: 7
            })
          };
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: function root(_ref32) {
          var gutterBottom = _ref32.ownerState.gutterBottom;
          return _extends({}, gutterBottom && {
            marginBottom: '0.5em'
          });
        }
      }
    }
  };
};